import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import phoneMetadata from "libphonenumber-js/metadata.min.json";

countries.registerLocale(en);

const COUNTRY_ALIASES = {
  JOD: "JO",
  JORDAN: "JO",
};

const arabicDigits = (value) =>
  String(value || "")
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)));

export const normalizeCountryCode = (country, fallback = "JO") => {
  const raw =
    country && typeof country === "object"
      ? country.code || country.value || country.name
      : country;
  const value = String(raw || "")
    .trim()
    .toUpperCase();
  if (COUNTRY_ALIASES[value]) return COUNTRY_ALIASES[value];
  if (/^[A-Z]{2}$/.test(value) && countries.isValid(value)) return value;
  if (/^[A-Z]{3}$/.test(value)) {
    const alpha2 = countries.alpha3ToAlpha2(value);
    if (alpha2) return alpha2;
  }
  const byName = countries.getAlpha2Code(String(raw || ""), "en");
  return byName || fallback;
};

export const getDefaultPhoneCountry = (country) => {
  if (country) return normalizeCountryCode(country);
  try {
    const user = JSON.parse(
      localStorage.getItem("connectToFitGymPanelUser") || "{}"
    );
    return normalizeCountryCode(user.country);
  } catch (error) {
    return "JO";
  }
};

const callingCodeFor = (country) => {
  const metadata = phoneMetadata.countries[country];
  return metadata && metadata[0];
};

const countryForCallingCode = (digits) => {
  const callingCode = Object.keys(phoneMetadata.country_calling_codes)
    .sort((left, right) => right.length - left.length)
    .find((code) => digits.indexOf(code) === 0);
  if (!callingCode) return {};
  const country = phoneMetadata.country_calling_codes[callingCode][0];
  return { callingCode, country };
};

const validNationalNumber = (number, country) => {
  const metadata = phoneMetadata.countries[country];
  if (!metadata) return false;
  const possibleLengths = metadata[3] || [];
  if (possibleLengths.indexOf(number.length) === -1) return false;
  return new RegExp(`^(?:${metadata[2]})$`).test(number);
};

export const normalizePhoneNumber = (value, country) => {
  const defaultCountry = getDefaultPhoneCountry(country);
  let input = arabicDigits(value).trim().replace(/^00/, "+");
  if (!input) return { value: "", valid: true, country: defaultCountry };

  const international = input.charAt(0) === "+";
  const digits = input.replace(/\D/g, "");
  let resolvedCountry = defaultCountry;
  let callingCode = callingCodeFor(defaultCountry);
  let nationalNumber = digits.replace(/^0+/, "");

  if (international) {
    const resolved = countryForCallingCode(digits);
    if (!resolved.callingCode) {
      return { value: input, valid: false, country: defaultCountry };
    }
    callingCode = resolved.callingCode;
    resolvedCountry = resolved.country;
    nationalNumber = digits.slice(callingCode.length);
  }

  const normalized = callingCode ? `+${callingCode}${nationalNumber}` : input;
  const valid = validNationalNumber(nationalNumber, resolvedCountry);
  return {
    value: valid ? normalized : input,
    valid,
    country: resolvedCountry,
  };
};

export const isValidPhoneInput = (value, country) => {
  if (!String(value || "").trim()) return true;
  return normalizePhoneNumber(value, country).valid;
};

export const phoneSearchValue = (value) =>
  arabicDigits(value).replace(/\D/g, "");
